import React, { useState, useRef } from "react";
import { toPng } from "html-to-image";
import { FaShieldAlt } from "react-icons/fa";

const DEFAULT_BG =
  "https://images.unsplash.com/photo-1518605368461-1ee7c532066d?q=80&w=1080&auto=format&fit=crop";

const ExportStandingsModal = ({ isOpen, onClose, standings, subcategory }) => {
  const exportRef = useRef(null);

  const [topLogo, setTopLogo] = useState(
    localStorage.getItem("exportTopLogo") || "",
  );
  const [bottomLogo, setBottomLogo] = useState(
    localStorage.getItem("exportBottomLogo") || "",
  );
  const [bgImage, setBgImage] = useState(
    localStorage.getItem("exportBgImage") || DEFAULT_BG,
  );
  const [isExporting, setIsExporting] = useState(false);

  // ============================================
  // LÓGICA PARA CONSTRUIR EL SUBTÍTULO
  // ============================================
  const getSubtitle = () => {
    let parts = [];

    // Si subcategory es un objeto (caso normal)
    if (subcategory && typeof subcategory !== "string") {
      // 1. Número de categoría (si existe y no es null/undefined)
      if (subcategory.number) {
        parts.push(`Categoría ${subcategory.number}`);
      }

      // 2. Nombre del grupo (si existe)
      if (subcategory.group_name) {
        const displayText = subcategory.display || subcategory.name || "";
        // Evitar duplicar si group_name es igual a display (ej: "principal" y "principal")
        if (displayText !== subcategory.group_name) {
          parts.push(subcategory.group_name);
        } else {
          // Si son iguales pero no hay número, mostramos solo una vez (el grupo)
          if (!subcategory.number && parts.length === 0) {
            parts.push(subcategory.group_name);
          }
        }
      }

      // 3. Fallback: si no hay número ni group_name, usar display o name
      if (parts.length === 0 && subcategory.display) {
        parts.push(subcategory.display);
      } else if (parts.length === 0 && subcategory.name) {
        parts.push(subcategory.name);
      }
    }
    // Si subcategory es un string (compatibilidad)
    else if (typeof subcategory === "string" && subcategory) {
      parts.push(subcategory);
    }

    // Fallback extremo: usar group_name del primer equipo de la tabla
    if (parts.length === 0 && standings.length > 0 && standings[0].group_name) {
      parts.push(standings[0].group_name);
    }

    return parts.join(" - ");
  };

  const subtitle = getSubtitle();

  // ============================================
  // MANEJADORES DE IMAGEN Y EXPORTACIÓN
  // ============================================
  const handleImageUpload = (e, setFunction, storageKey) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1500000) {
        alert(
          "La imagen es muy grande. Por favor sube una imagen menor a 1.5MB",
        );
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFunction(base64String);
        localStorage.setItem(storageKey, base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExport = async () => {
    if (!exportRef.current) return;
    setIsExporting(true);
    try {
      const dataUrl = await toPng(exportRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "transparent",
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      // Nombre del archivo usando el subtítulo o un valor por defecto
      const fileName = `Posiciones_${subtitle || "Torneo"}.png`;
      link.download = fileName;
      link.click();
    } catch (error) {
      alert("Hubo un error al generar la imagen.");
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row shadow-2xl">
        {/* Panel de Configuración (Izquierda) */}
        <div className="w-full md:w-1/3 p-6 bg-gray-50 overflow-y-auto border-r border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              Exportar Tabla de Posiciones
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-red-500 font-bold text-xl"
            >
              &times;
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Logo Superior
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setTopLogo, "exportTopLogo")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Imagen de Fondo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setBgImage, "exportBgImage")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <button
                onClick={() => {
                  setBgImage(DEFAULT_BG);
                  localStorage.removeItem("exportBgImage");
                }}
                className="mt-2 text-xs text-red-500 hover:underline"
              >
                Restaurar fondo por defecto
              </button>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Logo Inferior
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setBottomLogo, "exportBottomLogo")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <button
              onClick={handleExport}
              disabled={isExporting}
              className={`w-full py-3 rounded-xl font-bold text-white transition-colors ${
                isExporting ? "bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isExporting ? "Generando..." : "Descargar Imagen"}
            </button>
          </div>
        </div>

        {/* Vista Previa (Derecha) */}
        <div className="w-full md:w-2/3 p-6 bg-gray-200 overflow-y-auto flex items-start justify-center">
          <div
            ref={exportRef}
            className="relative w-full max-w-[600px] bg-blue-900 text-white overflow-hidden shadow-xl"
            style={{
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              fontFamily: "'Inter', sans-serif",
              padding: "40px 20px",
            }}
          >
            {/* Cabecera */}
            <div className="flex flex-col items-center mb-8">
              {topLogo && (
                <img
                  src={topLogo}
                  alt="Top Logo"
                  className="h-16 object-contain mb-4"
                />
              )}
              <h1
                className="text-5xl font-black tracking-tighter uppercase m-0 leading-none text-center"
                style={{ fontFamily: "Impact, sans-serif" }}
              >
                POSICIONES
              </h1>
              {/* Subtítulo dinámico con número de categoría y/o grupo */}
              {subtitle && (
                <h2 className="text-lg font-bold tracking-widest uppercase mt-2 text-blue-200 text-center">
                  {subtitle}
                </h2>
              )}
            </div>

            {/* Tabla */}
            <div className="w-full px-4 mb-12">
              <table className="w-full text-sm text-center font-medium">
                <thead>
                  <tr className="border-b-2 border-white/20 text-white/70">
                    <th className="pb-2 text-left">POS</th>
                    <th className="pb-2 text-left pl-4">CLUB</th>
                    <th className="pb-2">PJ</th>
                    <th className="pb-2">PG</th>
                    <th className="pb-2">PE</th>
                    <th className="pb-2">PP</th>
                    <th className="pb-2">GF</th>
                    <th className="pb-2">GC</th>
                    <th className="pb-2 font-bold text-white">PTS</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((team, index) => (
                    <tr key={team.team_id} className="border-b border-white/10">
                      <td className="py-3 text-left font-bold text-lg">
                        {index + 1}
                      </td>
                      <td className="py-3 text-left pl-4 flex items-center gap-3">
                        {team.picture_url ? (
                          <img
                            src={team.picture_url}
                            alt="club"
                            className="w-7 h-7 rounded-full bg-white object-cover"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white/80 border border-white/30">
                            <FaShieldAlt size={14} />
                          </div>
                        )}
                        <span className="font-bold">{team.team_name}</span>
                      </td>
                      <td className="py-3">{team.played}</td>
                      <td className="py-3">{team.won}</td>
                      <td className="py-3">{team.drawn}</td>
                      <td className="py-3">{team.lost}</td>
                      <td className="py-3">{team.goals_for}</td>
                      <td className="py-3">{team.goals_against}</td>
                      <td className="py-3 font-black text-lg">{team.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pie de página */}
            <div className="mt-auto pt-6 flex justify-center items-center">
              {bottomLogo && (
                <img
                  src={bottomLogo}
                  alt="Bottom Logo"
                  className="h-14 object-contain"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportStandingsModal;
